import { useEffect, useRef, useState } from "react";

import * as S from "./styles";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input } from "@/components/Input";

import {
  FaChevronLeft,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaUser,
} from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { ReactComponent as SvgFacebook } from "@/assets/svgs/facebook.svg";

import { ReactComponent as SuccessSvg } from "@/assets/svgs/success-circle.svg";

import { useAuth } from "@/context/AuthContext";
import { FutButton } from "@/components/FutButton";

import RoundInput from "@/components/RoundInput";
import {
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Modal,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  PASSWORD_MATCH,
  REQUIRED_FIELD,
  VALID_EMAIL,
} from "@/constants/yupErrors";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MdArrowBackIosNew,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { toast } from "react-toastify";
import { plans } from "@/constants/plans";
import { BsArrowLeft } from "react-icons/bs";
import { api } from "@/services/api";
import { catchError } from "@/utils/catchError";
import PlansChooser from "@/components/PlansChooser";
import { FaPix } from "react-icons/fa6";
import { setCookie } from "@/utils/cookies";

const loginSchema = yup.object({
  email: yup.string().required(REQUIRED_FIELD).email(VALID_EMAIL),
  password: yup.string().required(REQUIRED_FIELD),
});

type FormInputs = yup.InferType<typeof loginSchema>;

export default function Login() {
  const affiliatedId = localStorage.getItem("affiliatedId")
    ? localStorage.getItem("affiliatedId")
    : null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPassScreen, setResetPassScreen] = useState(false);
  const [planType, setPlanType] = useState("");
  const [userId, setUserId] = useState(0);
  const [step, setStep] = useState(0);
  const [modal, setModal] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);

  const loginForm = useForm<FormInputs>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const changePass = useForm({
    defaultValues: {
      email: "",
    },
  });

  const paymentForm = useForm({
    defaultValues: {
      payment: "CREDIT_CARD",
    },
  });

  async function submitLogin(data: FormInputs) {
    const { email, password } = data;
    setIsSubmitting(true);

    const hasError = await signIn({ email, password });
    if (hasError?.error.status === 402) {
      setModal(true);
      setUserId(hasError.error.userId);
    }

    setIsSubmitting(false);
  }

  async function submitChangePass(data: { email: string }) {
    try {
      setIsSubmitting(true);
      await api.post("/user/requestNewPassword", {
        email: data.email,
      });
      toast.success(
        "Email enviado com sucesso! Cheque sua caixa de mensagens ou a caixa de spam"
      );
      setResetPassScreen(false);
      setIsSubmitting(false);
    } catch (e) {
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error("Ocorreu um erro ao tentar enviar o email");
    }
  }

  const changeStep = (toStep: "prev" | "next") => {
    if (containerRef.current) {
      containerRef.current.style.opacity = "0";

      setTimeout(() => {
        if (toStep === "next") {
          setStep((prev) => prev + 1);
        }

        if (toStep === "prev") {
          setStep((prev) => prev - 1);
        }

        containerRef.current!.style.opacity = "1";
        setIsSubmitting(false);
      }, 400);
    }
  };

  const planHandler = async (plan: keyof typeof plans) => {
    setIsSubmitting(true);
    setPlanType(plan);
    changeStep("next");
  };

  const submitPayment = async (data: { payment: string }) => {
    try {
      setIsSubmitting(true);
      const response = await api.post("/payment/checkout", {
        lineItems: [
          { price: plans[planType as keyof typeof plans], quantity: 1 },
        ],
        userId,
        type_payment: data.payment,
      });
      if (data.payment === "PIX") {
        setCookie(
          "payment_data",
          JSON.stringify({
            userId: userId,
            planType: planType,
            pixKey: response.data.body,
          }),
          1
        );

        navigate("/telaPix");
      }

      if (data.payment === "CREDIT_CARD") {
        changeStep("next");
        setIsSubmitting(false);
        setTimeout(() => {
          window.location.replace(response.data.body);
        }, 5000);
      }

      setIsSubmitting(false);
    } catch (e) {
      setStep(0);
      setModal(false);
      setIsSubmitting(false);
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error("Ocorreu um erro inesperado ao enviar sua solicitação");
    }
  };

  const modalSteps = [
    {
      component: (
        <S.ValidSignature>
          <h2>Sua Assinatura Não é Valida</h2>

          <p>
            Parece que sua assinatura acabou ou você ainda não fez o pagamento,
            volte agora a ser um membro da equipe e tenha todos os benefícios
            que a Futscore te oferece.
          </p>

          <FutButton fullWidth onClick={() => changeStep("next")}>
            Assinar Agora
          </FutButton>
        </S.ValidSignature>
      ),
    },
    {
      component: (
        <>
          <PlansChooser
            planHandler={(plan) => planHandler(plan)}
            disabled={isSubmitting}
          />
        </>
      ),
    },
    {
      component: (
        <S.PaymentContainer onSubmit={paymentForm.handleSubmit(submitPayment)}>
          <Controller
            name="payment"
            control={paymentForm.control}
            render={({ field }) => (
              <RadioGroup sx={{ width: "250px" }} {...field}>
                {/* <S.RadioContainer>
                  <Radio value="CREDIT_CARD" />
                  <S.RadioNameContainer>
                    <BsFillCreditCard2BackFill />
                    Cartão de Crédito
                  </S.RadioNameContainer>
                </S.RadioContainer> */}

                <S.RadioContainer>
                  <Radio value="PIX" />
                  <S.RadioNameContainer>
                    <FaPix />
                    Pix
                  </S.RadioNameContainer>
                </S.RadioContainer>

                <FutButton
                  type="submit"
                  isLoading={isSubmitting}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Finalizar Compra
                </FutButton>
              </RadioGroup>
            )}
          />
        </S.PaymentContainer>
      ),
    },
    {
      component: (
        <>
          {isSubmitting ? (
            <S.SuccessContainer style={{ padding: 30 }}>
              <CircularProgress />
            </S.SuccessContainer>
          ) : (
            <S.SuccessContainer>
              <SuccessSvg />
              <h2>
                Em instantes você será redirecionado para tela de checkout
              </h2>
            </S.SuccessContainer>
          )}
        </>
      ),
    },
  ];

  const loginErrors = loginForm.formState.errors;
  const changePassErrors = changePass.formState.errors;

  return (
    <>
      <S.FormContainer>
        <S.LoginBackground />

        <S.InputsContainer>
          <S.InputCover>
            {resetPassScreen ? (
              <>
                <S.TitleWrapper>
                  <span
                    onClick={() => {
                      setResetPassScreen(false);
                    }}
                  >
                    <MdArrowBackIosNew />
                  </span>
                  <h1>
                    Mudar Senha<span>.</span>
                  </h1>
                </S.TitleWrapper>
                <p>Coloque seu email abaixo e enviaremos um link para ele:</p>
                <form>
                  <RoundInput
                    inputName="email"
                    control={changePass.control}
                    topLabel="Email"
                    fullWidth
                    size="small"
                    errorText={changePassErrors.email}
                    autoComplete="email"
                  />
                  <FutButton
                    sx={{ mt: 3 }}
                    onClick={changePass.handleSubmit(submitChangePass)}
                    isLoading={isSubmitting}
                    fullWidth
                  >
                    Entrar
                  </FutButton>
                </form>
              </>
            ) : (
              <>
                <h1>
                  Login<span>.</span>
                </h1>
                {/* <S.OuterLogin>
                    <button>
                      <FcGoogle /> Google
                    </button>
                    <button>
                      <SvgFacebook /> Facebook
                    </button>
                  </S.OuterLogin>
                  <Divider
                    sx={{
                      span: {
                        color: (props) => props.palette.fontColor.main,
                      },
                    }}
                  >
                    Ou
                  </Divider> */}
                <form
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      loginForm.handleSubmit(submitLogin)();
                    }
                  }}
                >
                  <RoundInput
                    inputName="email"
                    control={loginForm.control}
                    topLabel="Email"
                    fullWidth
                    size="small"
                    errorText={loginErrors.email}
                    autoComplete="email"
                  />
                  <RoundInput
                    inputName="password"
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? (
                            <MdVisibilityOff />
                          ) : (
                            <MdVisibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    control={loginForm.control}
                    topLabel="Senha"
                    fullWidth
                    size="small"
                    type={showPassword ? "text" : "password"}
                    errorText={loginErrors.password}
                    autoComplete="current-password"
                  />
                  <S.ResetPassTxt
                    onClick={() => {
                      setResetPassScreen((prev) => !prev);
                    }}
                  >
                    Esqueceu a senha?
                  </S.ResetPassTxt>
                  <FutButton
                    sx={{ mb: 3 }}
                    onClick={loginForm.handleSubmit(submitLogin)}
                    isLoading={isSubmitting}
                    fullWidth
                  >
                    Entrar
                  </FutButton>
                </form>
                <S.InputsFooter>
                  <span> Não tem conta? </span>
                  <NavLink to={`/register?af=1`}>
                    Cadastre-se!
                  </NavLink>
                </S.InputsFooter>
              </>
            )}
          </S.InputCover>
        </S.InputsContainer>
      </S.FormContainer>
      <Modal open={modal}>
        <S.ModalWrapper>
          <S.ModalContainer ref={containerRef}>
            {isSubmitting ? null : (
              <S.ModalActions>
                {step > 0 && step < 3 ? (
                  <S.ModalButton onClick={() => changeStep("prev")}>
                    <BsArrowLeft />
                  </S.ModalButton>
                ) : null}

                <S.ModalButton onClick={() => setModal(false)}>
                  <span>X</span>
                </S.ModalButton>
              </S.ModalActions>
            )}
            {modalSteps[step].component}
          </S.ModalContainer>
        </S.ModalWrapper>
      </Modal>
    </>
  );
}
